import { API_BASE, type IntakeApi } from '../api/client';
import { ApiError } from '../api/errors';
import { currentEnquiry } from '../funnel/session';
import type { AnalyticsEvent, AnalyticsSink } from './analytics';

/**
 * The Intake API's own funnel analytics (`postEvents` → `enquiry_events`, architecture.md §3),
 * so completion can be measured without a third party. Only `{ screen, event, at }` goes: the
 * contract has no props, so nothing personal can leave this way.
 *
 * - `screen_view` becomes `{ screen, event: 'view' }`; any other event keeps its name and goes
 *   on the screen its name starts with (`s9_…` → S9), else on the screen last viewed.
 * - Events wait in a queue and go in batches: every `flushMs`, at `maxBatch`, and with
 *   `sendBeacon` when the page is hidden (tab switch, app switch, close). Events from before
 *   the enquiry exists (S1, S2) wait for its id and go with the first batch that has one.
 * - A 4xx (no grant, a gone enquiry) drops the batch; a network or 5xx failure puts it back
 *   once. Never throws, never blocks the funnel.
 */
export interface EnquiryEventsOptions {
  api: IntakeApi;
  /** Which enquiry an event belongs to (default: the URL's `/e/<id>` or `/c/<id>`, else the tab's check). */
  enquiryId?: () => string | null;
  flushMs?: number;
  maxBatch?: number;
  /** `navigator.sendBeacon`, injectable for tests. */
  beacon?: (url: string, body: Blob) => boolean;
  now?: () => Date;
}

interface Queued {
  enquiryId: string | null;
  screen: string;
  event: string;
  at: string;
  retried?: boolean;
}

export interface EnquiryEventsSink extends AnalyticsSink {
  /** Send what's queued now; `beacon` for page hide (no promise to wait on). */
  flush(opts?: { beacon?: boolean }): Promise<void>;
  /** Stop the timer and the page listeners. */
  dispose(): void;
}

/* The contract's FunnelEvent patterns: anything else would 422 the whole batch. */
const SCREEN = /^S[0-9]{1,2}[a-z]?(-[a-z-]+)?$/;
const EVENT = /^[a-z][a-z0-9_]{0,63}$/;
const MAX_QUEUE = 200;
const CONTRACT_MAX = 50;

export function enquiryIdFromLocation(): string | null {
  const m = /\/(?:e|c)\/([0-9A-HJKMNP-TV-Z]{26})(?:\/|$)/.exec(globalThis.location?.pathname ?? '');
  return m?.[1] ?? currentEnquiry.get();
}

/** The screen an event without its own belongs to: `s9_passengers_saved` → S9. */
function screenFromName(name: string): string | null {
  const m = /^s(\d{1,2}[a-z]?)_/.exec(name);
  return m ? `S${m[1]}` : null;
}

export function createEnquiryEventsSink({
  api,
  enquiryId = enquiryIdFromLocation,
  flushMs = 4000,
  maxBatch = 20,
  beacon = typeof navigator !== 'undefined' && navigator.sendBeacon
    ? (url, body) => navigator.sendBeacon(url, body)
    : undefined,
  now = () => new Date(),
}: EnquiryEventsOptions): EnquiryEventsSink {
  let queue: Queued[] = [];
  let screen: string | null = null;
  let timer: ReturnType<typeof setTimeout> | null = null;

  const schedule = () => {
    if (!timer) timer = setTimeout(() => void flush(), flushMs);
  };

  function track(e: AnalyticsEvent) {
    const isView = e.name === 'screen_view';
    // A screen's own events name it (`s5_shown`): a screen tracks from its mount effect, which
    // runs before the shell's screen_view, so "the screen last viewed" would be the one before.
    const s = isView
      ? String((e as { screen?: unknown }).screen ?? '')
      : (screenFromName(e.name) ?? screen ?? '');
    if (isView) screen = SCREEN.test(s) ? s : null;
    const event = isView ? 'view' : e.name;
    if (!s || !SCREEN.test(s) || !EVENT.test(event)) return;
    queue.push({ enquiryId: enquiryId(), screen: s, event, at: now().toISOString() });
    if (queue.length > MAX_QUEUE) queue = queue.slice(-MAX_QUEUE);
    if (queue.filter((q) => q.enquiryId).length >= maxBatch) void flush();
    else schedule();
  }

  /** Takes what can go now, per enquiry: events without an id join the first enquiry's batch. */
  function take(): Map<string, Queued[]> {
    const id = queue.find((q) => q.enquiryId)?.enquiryId ?? enquiryId();
    const batches = new Map<string, Queued[]>();
    if (!id) return batches;
    const rest: Queued[] = [];
    for (const q of queue) {
      const key = q.enquiryId ?? id;
      const b = batches.get(key) ?? [];
      if (b.length < CONTRACT_MAX) {
        b.push(q);
        batches.set(key, b);
      } else rest.push(q);
    }
    queue = rest;
    return batches;
  }

  async function flush({ beacon: useBeacon = false } = {}) {
    if (timer) clearTimeout(timer);
    timer = null;
    const batches = take();
    for (const [id, items] of batches) {
      const body = { events: items.map(({ screen: s, event, at }) => ({ screen: s, event, at })) };
      if (useBeacon && beacon) {
        const url = `${API_BASE}/enquiries/${encodeURIComponent(id)}/events`;
        try {
          if (beacon(url, new Blob([JSON.stringify(body)], { type: 'application/json' }))) continue;
        } catch {
          /* fall through to fetch */
        }
      }
      try {
        await api.postEvents(id, body);
      } catch (e) {
        const clientError = e instanceof ApiError && e.status < 500;
        if (!clientError)
          queue.unshift(...items.filter((q) => !q.retried).map((q) => ({ ...q, retried: true })));
      }
    }
    if (queue.some((q) => q.enquiryId)) schedule();
  }

  const onHide = () => {
    if (document.visibilityState === 'hidden') void flush({ beacon: true });
  };
  const onPageHide = () => void flush({ beacon: true });
  if (typeof document !== 'undefined') {
    document.addEventListener('visibilitychange', onHide);
    window.addEventListener('pagehide', onPageHide);
  }

  return {
    track,
    flush,
    dispose() {
      if (timer) clearTimeout(timer);
      timer = null;
      if (typeof document !== 'undefined') {
        document.removeEventListener('visibilitychange', onHide);
        window.removeEventListener('pagehide', onPageHide);
      }
    },
  };
}
